import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Button, Empty, Skeleton } from "antd";
import { DeleteOutlined, EditOutlined, MailOutlined, PauseCircleOutlined, PhoneOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";

import apiClient from "../../../../helpers/apiClient";
import CarDetailFieldTable from "../../../cars/components/cardetails/CarDetailFieldTable";
import { CopyButton } from "../../../../components/data-table";
import EmployeeAvatar from "./EmployeeAvatar";
import EmployeePageHeader from "./EmployeePageHeader";
import EmployeeStatusTag from "./EmployeeStatusTag";
import useEmployeeActions from "./useEmployeeActions";
import {
    EMPLOYEE_LIST_PATH,
    employeeEditPath,
    getEmployeeFullName,
    isEmployeeActive,
} from "./employeeHelpers";

const NA = "N/A";
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : null);

function SectionCard({ children }) {
    return <div className="min-w-0 rounded-2xl border border-[#f0f0f0] bg-white pt-4">{children}</div>;
}

SectionCard.propTypes = { children: PropTypes.node };

function ContactLine({ icon, value, href }) {
    if (!value) return null;
    return (
        <div className="sc-copy-scope flex min-w-0 items-center gap-2 text-sm text-gray-600">
            <span className="text-gray-400">{icon}</span>
            <a href={href} className="truncate text-gray-700 hover:text-[#5D5DF6]">
                {value}
            </a>
            <CopyButton value={value} />
        </div>
    );
}

ContactLine.propTypes = { icon: PropTypes.node, value: PropTypes.string, href: PropTypes.string };

function EmployeeDetailsPage({ match }) {
    const employeeId = match?.params?.id;
    const { t } = useTranslation("settings");
    const history = useHistory();
    const { changeStatus, deleteEmployee, getStatusActionLabel } = useEmployeeActions();

    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const fetchEmployee = useCallback(async () => {
        try {
            const res = await apiClient.get(`api/employee/${employeeId}`);
            if (res?.data?.success && res.data.data) {
                setEmployee(res.data.data);
                setNotFound(false);
            } else {
                setNotFound(true);
            }
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [employeeId]);

    useEffect(() => {
        setLoading(true);
        fetchEmployee();
    }, [fetchEmployee]);

    const goToList = () => history.push(EMPLOYEE_LIST_PATH);
    const active = isEmployeeActive(employee);
    const fullName = getEmployeeFullName(employee);

    const personalRows = [
        { label: t("employees.firstName"), value: employee?.firstName },
        { label: t("employees.lastName"), value: employee?.lastName },
        { label: t("employees.email"), value: employee?.email },
        { label: t("employees.phone"), value: employee?.phone },
        { label: t("employees.dateOfBirth"), value: employee?.dateOfBirth },
    ];

    const businessRows = [
        { label: t("employees.company"), value: employee?.company?.name },
        { label: t("employees.registrationNumber"), value: employee?.registrationNumber },
        { label: t("employees.taxNumber"), value: employee?.taxNumber },
        { label: t("employees.status"), render: () => <EmployeeStatusTag employee={employee} /> },
    ];

    const address = employee?.address || {};
    const addressRows = [
        { label: t("employees.addressLine1"), value: address.address },
        { label: t("employees.addressLine2"), value: address.address2 },
        { label: t("employees.city"), value: address.city },
        { label: t("employees.state"), value: address.state },
        { label: t("employees.country"), value: address.country },
        { label: t("employees.zipCode"), value: address.zip },
    ];

    const headerActions =
        employee && !loading ? (
            <>
                <Button
                    shape="round"
                    icon={active ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                    onClick={() => changeStatus(employee, fetchEmployee)}>
                    {getStatusActionLabel(employee)}
                </Button>
                <Button
                    shape="round"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => deleteEmployee(employee, goToList)}>
                    {t("employees.deleteEmployee")}
                </Button>
                <Button
                    type="primary"
                    shape="round"
                    icon={<EditOutlined />}
                    onClick={() => history.push(employeeEditPath(employeeId))}>
                    {t("employees.editDetails")}
                </Button>
            </>
        ) : null;

    const renderBody = () => {
        if (loading) {
            return (
                <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(320px,380px)_minmax(0,1fr)]">
                    <div className="flex flex-col items-center gap-4 rounded-2xl border border-[#f0f0f0] bg-white p-8">
                        <Skeleton.Avatar active size={112} />
                        <Skeleton active paragraph={{ rows: 3 }} />
                    </div>
                    <div className="rounded-2xl border border-[#f0f0f0] bg-white p-8">
                        <Skeleton active paragraph={{ rows: 10 }} />
                    </div>
                </div>
            );
        }

        if (notFound || !employee) {
            return (
                <div className="rounded-2xl border border-[#f0f0f0] bg-white py-16">
                    <Empty description={t("employees.notFound")}>
                        <Button shape="round" onClick={goToList}>
                            {t("employees.backToEmployees")}
                        </Button>
                    </Empty>
                </div>
            );
        }

        return (
            <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(320px,380px)_minmax(0,1fr)]">
                <aside className="flex min-w-0 flex-col self-start rounded-2xl border border-[#f0f0f0] bg-white">
                    <div className="flex flex-col items-center gap-3 border-b border-[#f0f0f0] px-6 py-8 text-center">
                        <EmployeeAvatar
                            employee={employee}
                            size={112}
                            preview
                            className="shadow-[0_4rem_2rem_-3.4rem_#5d5dff]"
                        />
                        <div className="min-w-0">
                            <h2 className="fontbold break-words text-lg text-[#121212]">{fullName || NA}</h2>
                            <div className="mt-2 flex justify-center">
                                <EmployeeStatusTag employee={employee} />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 px-6 py-4">
                        <ContactLine icon={<MailOutlined />} value={employee.email} href={`mailto:${employee.email}`} />
                        <ContactLine icon={<PhoneOutlined />} value={employee.phone} href={`tel:${employee.phone}`} />
                    </div>
                    <dl className="grid grid-cols-2 gap-3 border-t border-[#f0f0f0] px-6 py-4 text-sm">
                        <div>
                            <dt className="text-xs text-gray-400">{t("employees.addedOn")}</dt>
                            <dd className="mt-0.5 text-gray-700">{formatDate(employee.createdAt) || NA}</dd>
                        </div>
                        <div>
                            <dt className="text-xs text-gray-400">{t("employees.lastUpdated")}</dt>
                            <dd className="mt-0.5 text-gray-700">{formatDate(employee.updatedAt) || NA}</dd>
                        </div>
                    </dl>
                </aside>

                <div className="flex min-w-0 flex-col gap-3">
                    <SectionCard>
                        <CarDetailFieldTable
                            title={t("employees.personalInformation")}
                            rows={personalRows}
                            naFallback={NA}
                            column={2}
                        />
                    </SectionCard>
                    <SectionCard>
                        <CarDetailFieldTable
                            title={t("employees.businessInformation")}
                            rows={businessRows}
                            naFallback={NA}
                            column={2}
                        />
                    </SectionCard>
                    <SectionCard>
                        <CarDetailFieldTable
                            title={t("employees.address")}
                            rows={addressRows}
                            naFallback={NA}
                            column={2}
                        />
                    </SectionCard>
                </div>
            </div>
        );
    };

    return (
        <div className="flex min-w-0 flex-col gap-3">
            <EmployeePageHeader title={t("employees.employeeDetails")} onBack={goToList} actions={headerActions} />
            {renderBody()}
        </div>
    );
}

EmployeeDetailsPage.propTypes = {
    match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string }) }),
};

export default EmployeeDetailsPage;
